"use client"

import { useEffect, useState } from "react"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { config } from "@/config"
import { BookOpen, Home, Library, Menu, WandSparkles } from "lucide-react"

import { cn } from "@/lib/utils"
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"
import { BrandIcon } from "@/components/brand-icon"
import { ThemeToggle } from "@/components/theme-toggle"

const NAV_ITEMS = [
  { href: "/", label: "Home", icon: Home },
  { href: "/generate", label: "Generator", icon: WandSparkles },
  { href: "/guides", label: "Guides", icon: BookOpen },
  { href: "/snippets", label: "Snippets", icon: Library },
]

function isActivePath(pathname: string, href: string) {
  return href === "/" ? pathname === "/" : pathname.startsWith(href)
}

export function Header() {
  const pathname = usePathname()
  const [isAtTop, setIsAtTop] = useState(true)

  useEffect(() => {
    const onScroll = () => {
      setIsAtTop(window.scrollY === 0)
    }

    onScroll()
    window.addEventListener("scroll", onScroll, { passive: true })

    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  return (
    <nav
      className={cn(
        "top-0 z-50 w-full transition-colors",
        isAtTop
          ? "absolute left-0 bg-transparent"
          : "bg-background/65 fixed left-0 border-b backdrop-blur-md"
      )}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6">
        <Link
          href="/"
          className="flex min-w-0 items-center gap-2 transition-opacity hover:opacity-80"
        >
          <BrandIcon priority />
          <span className="truncate text-base font-semibold tracking-tight sm:text-lg">
            {config.name}
          </span>
        </Link>
        <div className="flex items-center gap-1 sm:gap-2">
          <div className="hidden h-16 items-center gap-1 md:flex">
            {NAV_ITEMS.slice(1).map((item) => {
              const isActive = isActivePath(pathname, item.href)

              return (
                <Link
                  key={item.href}
                  href={item.href}
                  aria-current={isActive ? "page" : undefined}
                  className={cn(
                    "hover:text-foreground mx-3 inline-flex h-full items-center text-sm font-medium transition-colors",
                    isActive ? "text-foreground" : "text-muted-foreground"
                  )}
                >
                  <span
                    className={cn(
                      "border-b-2 py-1 transition-colors",
                      isActive ? "border-primary" : "border-transparent"
                    )}
                  >
                    {item.label}
                  </span>
                </Link>
              )
            })}
          </div>
          <ThemeToggle />
          <Sheet>
            <SheetTrigger asChild>
              <button
                type="button"
                aria-label="Open navigation menu"
                className="text-primary bg-primary/5 hover:bg-primary/10 inline-flex size-8 items-center justify-center rounded-lg transition-colors md:hidden"
              >
                <Menu className="size-4" />
              </button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="w-[min(20rem,85vw)] gap-0 p-0 md:hidden"
            >
              <SheetHeader className="border-b p-5">
                <div className="flex items-center gap-2">
                  <BrandIcon />
                  <SheetTitle>{config.name}</SheetTitle>
                </div>
                <SheetDescription className="text-left">
                  Create, learn, and reuse snippets.
                </SheetDescription>
              </SheetHeader>
              <div className="flex flex-col gap-1 p-3">
                {NAV_ITEMS.map((item) => {
                  const Icon = item.icon
                  const isActive = isActivePath(pathname, item.href)

                  return (
                    <SheetClose key={item.href} asChild>
                      <Link
                        href={item.href}
                        aria-current={isActive ? "page" : undefined}
                        className={cn(
                          "flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium transition-colors",
                          isActive
                            ? "bg-primary/10 text-primary"
                            : "text-muted-foreground hover:bg-accent hover:text-foreground"
                        )}
                      >
                        <Icon className="size-4" />
                        {item.label}
                        {isActive && (
                          <span className="bg-primary ml-auto size-1.5 rounded-full" />
                        )}
                      </Link>
                    </SheetClose>
                  )
                })}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  )
}
